
import { useState } from 'react';

export function BenefitsSection() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const benefits = [
    {
      title: '電気の自給自足ができるってホント？',
      icon: 'ri-sun-line',
      image: 'https://public.readdy.ai/ai/img_res/2511f9f8d495d8a02a56b19e9da56e65.jpg',
      content: [
        '太陽光発電が稼働している時間帯は自動的に自宅で消費する電気を自給自足することができます。そのため、昼間に利用する電気代を削減することができます。',
        'エコキュートなどをご使用の電化プランのご家庭の場合は、昼間の電気代が割高な料金単価になっているため、太陽光発電で自給自足できれば、割高な電気を買うことがなくなり電気代の大幅削減が期待できます。',
        'また電気代には「再エネ賦課金」が料金単価に上乗せされています。太陽光発電で電気を自給自足することにより買電量を減らすことができるので、この再エネ賦課金を削減することができます。'
      ],
      color: 'emerald'
    },
    {
      title: '電気を貯められるってホント？',
      icon: 'ri-battery-charge-line',
      image: 'https://static.readdy.ai/image/9914a1d99036b66c957f87afe7b3976a/39aa15997a773d6ced260ddcc0c21b01.jpeg',
      content: [
        '日中に発電して余った電気を、電力会社に売電することができます。',
        'また蓄電池を設置した場合は余った電気を貯めることができます。太陽光の発電できない夜の時間帯には、日中蓄電池に貯めた電気を使用することで電気の購入量を減らし、さらに電気代を削減することができます。',
        '以前は割安だった夕方～夜間の電気料金単価も、現在は高くなっているので、蓄電池を設置した場合の経済効果がさらに大きくなっています。',
        '現在太陽光を使用中のお宅が後付けで蓄電池を設置したり、太陽光の導入時に蓄電池もセットで考えるお客様も多くなっています。'
      ],
      color: 'blue'
    },
    {
      title: '停電時でも電気が使えるってホント？',
      icon: 'ri-flashlight-line',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20family%20safely%20using%20electricity%20at%20home%20during%20power%20outage%20with%20solar%20panels%20and%20battery%20backup%20system%2C%20warm%20indoor%20lighting%20from%20stored%20solar%20energy%20during%20nighttime%20blackout%2C%20emergency%20preparedness%20and%20energy%20security%20concept%20illustration%20with%20simple%20clean%20background%2C%20professional%20lifestyle%20photography%20showing%20peace%20of%20mind&width=800&height=600&seq=benefit-emergency-power-003&orientation=landscape',
      content: [
        '太陽光発電と蓄電池を導入しておけば、太陽が出ている日中は電気を使うことができますし、さらに蓄電池に電気を蓄えておけるので夜間でも電気を使えます。',
        '近年、豪雨や台風などで停電が長期化するケースが増えていますので、そのようなときに電気が使えるよう備えておけば安心です。'
      ],
      color: 'amber'
    }
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      emerald: {
        bg: 'bg-emerald-50',
        iconBg: 'bg-emerald-100',
        iconText: 'text-emerald-600',
        badge: 'bg-emerald-500',
        border: 'border-emerald-200'
      },
      blue: {
        bg: 'bg-sky-50',
        iconBg: 'bg-sky-100',
        iconText: 'text-sky-600',
        badge: 'bg-sky-500',
        border: 'border-sky-200'
      },
      amber: {
        bg: 'bg-amber-50',
        iconBg: 'bg-amber-100',
        iconText: 'text-amber-600',
        badge: 'bg-amber-500',
        border: 'border-amber-200'
      }
    };
    return colors[color as keyof typeof colors];
  };

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* セクションヘッダー */}
        <div className="text-center mb-16">
          <div className="inline-block bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-lightbulb-line mr-1"></i>
            太陽光発電の3つのメリット
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            知っておきたい！<br className="sm:hidden" />太陽光発電の魅力
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            電気代削減だけじゃない。太陽光発電で実現できる、<br className="hidden sm:block" />
            快適で安心な暮らしをご紹介します。
          </p>
        </div>

        {/* メリットカード */}
        <div className="space-y-12">
          {benefits.map((benefit, index) => {
            const colors = getColorClasses(benefit.color);
            const isEven = index % 2 === 0;
            const isExpanded = expandedIndex === index;

            return (
              <div
                key={index}
                className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
              >
                {/* 画像エリア */}
                <div className="w-full lg:w-1/2">
                  <div className="relative rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
                    <div className="w-full h-80">
                      <img
                        src={benefit.image}
                        alt={benefit.title}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                    <div className={`absolute top-4 ${isEven ? 'left-4' : 'right-4'}`}>
                      <div className={`${colors.badge} text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg flex items-center space-x-2`}>
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className={`${benefit.icon} text-lg`}></i>
                        </div>
                        <span>メリット {index + 1}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* テキストエリア */}
                <div className="w-full lg:w-1/2">
                  <div className={`${colors.bg} rounded-2xl p-8 border-2 ${colors.border}`}>
                    <div className="flex items-start space-x-4 mb-6">
                      <div className={`${colors.iconBg} rounded-xl p-3 flex-shrink-0`}>
                        <div className="w-8 h-8 flex items-center justify-center">
                          <i className={`${benefit.icon} text-2xl ${colors.iconText}`}></i>
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 leading-tight pt-2">
                        {benefit.title}
                      </h3>
                    </div>

                    <div className="space-y-4">
                      {benefit.content.map((paragraph, pIndex) => {
                        // 最初の段落のみ表示、または全て表示
                        if (!isExpanded && pIndex > 0) return null;
                        
                        return (
                          <p 
                            key={pIndex} 
                            className={`text-gray-700 leading-relaxed ${!isExpanded && pIndex === 0 ? 'line-clamp-4' : ''}`}
                          >
                            {paragraph}
                          </p>
                        );
                      })}
                    </div>

                    {/* 続きを読むボタン */}
                    {benefit.content.length > 1 && (
                      <button
                        onClick={() => setExpandedIndex(isExpanded ? null : index)}
                        className={`mt-4 flex items-center space-x-1 ${colors.iconText} hover:underline font-medium transition-all duration-200`}
                      >
                        <span>{isExpanded ? '閉じる' : '続きを読む'}</span>
                        <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg`}></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              設置費用などもっと詳しく知りたい方へ
            </h3>
            <p className="text-gray-700 mb-6 leading-relaxed">
              太陽光発電は経済的効果に対して費用対効果が最も重要なポイントです。<br className="hidden sm:block" />
              お客様のご自宅に最適なシステムについて、専門スタッフがご相談を承ります。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => {
                  const contactSection = document.getElementById('contact-section');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap"
              >
                <i className="ri-calculator-line mr-2"></i>
                無料診断を申し込む
              </button>
              <a
                href="tel://0762553483"
                className="w-full sm:w-auto bg-white hover:bg-gray-50 text-emerald-600 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-md hover:shadow-lg border-2 border-emerald-500 whitespace-nowrap"
              >
                <i className="ri-phone-fill mr-2"></i>
                電話で相談する
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
